import { html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { SlotController } from '../common/controllers/SlotController.js'
import { observe } from '../common/decorators/observe.js'

import componentStyle from '../common/styles/Component.css'
import style from './AsideTrigger.css'
import '../button/Button.js'
import '../icon/Icon.js'
import '../tooltip/Tooltip.js'

let asideTriggerTooltipIdCounter = 0

/**
 * Rail button that toggles a `<nord-aside-drawer>` inside `<nord-aside>`,
 * or navigates like a link when `href` is set.
 *
 * @status new
 * @category action
 * @slot - Overrides the `icon` attribute for custom leading content.
 * @slot tooltip - Overrides the tooltip body (falls back to `label`).
 *   Lets consumers add icons, shortcut hints, etc. — anything beyond the
 *   plain string label.
 *
 * @parent Aside
 */
@customElement('nord-aside-trigger')
export default class AsideTrigger extends LitElement {
  static styles = [componentStyle, style]

  private defaultSlot = new SlotController(this)
  private tooltipSlot = new SlotController(this, 'tooltip')

  /**
   * The id of the `<nord-aside-drawer>` this trigger toggles.
   * Mutually exclusive with `href` — when both are set, `href` wins and
   * the trigger acts as a link instead of a drawer toggle.
   */
  @property({ reflect: true }) drawer: string = ''

  /**
   * When provided, renders the trigger as a link that navigates to the
   * given URL instead of toggling a drawer. Mutually exclusive with
   * `drawer`. Mirrors `nord-button`'s link mode, so middle-click,
   * cmd/ctrl-click and right-click behave like a real anchor.
   */
  @property({ reflect: true }) href?: string

  /**
   * When provided together with a href property, determines where to open
   * the linked URL. Forwarded to the underlying anchor.
   */
  @property() target?: '_self' | '_blank' | '_parent' | '_top'

  /**
   * When provided together with a href property, sets the relationship
   * between the linked URL and the current document, e.g. “noopener
   * noreferrer” when linking to untrusted content in a new tab.
   */
  @property() rel?: string

  /**
   * Nord icon name. Shortcut for the common case; equivalent to placing
   * `<nord-icon name="…">` in the default slot.
   */
  @property({ reflect: true }) icon?: string

  /**
   * Accessible label (used for `aria-label` on the host).
   */
  @property({ reflect: true }) label: string = ''

  /**
   * Optional badge content (forwarded to inner `<nord-badge>`).
   */
  @property({ reflect: true }) badge?: string | number

  /**
   * Makes the trigger disabled. Takes precedence over the target
   * drawer's `disabled` state.
   */
  @property({ type: Boolean, reflect: true }) disabled: boolean = false

  /**
   * Whether to render a `<nord-tooltip>` showing `label` on hover/focus.
   * Defaults to `true` — set `tooltip="false"` (or omit `label`) to opt out.
   */
  @property({ type: Boolean, reflect: true }) tooltip: boolean = true

  /**
   * Whether the ancestor `<nord-aside>`'s `active-drawer` currently
   * matches this trigger's `drawer` id.
   * @internal
   */
  @state() private active: boolean = false

  /**
   * Whether the target drawer exists and is not disabled. Drives
   * `aria-disabled` on the host.
   * @internal
   */
  @state() private targetDisabled: boolean = false

  private observedAside?: HTMLElement
  private readonly tooltipId = `n-aside-trigger-tip-${++asideTriggerTooltipIdCounter}`

  render() {
    const showCustomLeading = !this.defaultSlot.isEmpty
    const showIcon = !showCustomLeading && !!this.icon
    const showBadge = this.badge !== undefined && this.badge !== null && this.badge !== ''

    // Tooltip is rendered when explicitly enabled AND there's something to
    // show — either the `label` attr (default body) or a custom `tooltip`
    // slot.
    const hasTooltipSlot = !this.tooltipSlot.isEmpty
    const showTooltip = this.tooltip && (!!this.label || hasTooltipSlot)
    return html`
      <div class="n-aside-trigger">
        <nord-button
          square
          size="s"
          variant="plain"
          type="button"
          href=${this.href || nothing}
          target=${(this.isLink && this.target) || nothing}
          rel=${(this.isLink && this.rel) || nothing}
          aria-label=${this.label || nothing}
          aria-describedby=${showTooltip ? this.tooltipId : nothing}
          ?disabled=${this.disabled}
          @click=${this.handleClick}
        >
          ${showCustomLeading ? html`<slot></slot>` : nothing}
          ${showIcon ? html`<nord-icon name=${this.icon!} size="m"></nord-icon>` : nothing}
        </nord-button>
        ${showBadge ? html`<span class="n-aside-trigger-dot" aria-hidden="true"></span>` : nothing}
        ${showTooltip
          ? html`
              <nord-tooltip id=${this.tooltipId} position="inline-start">
                <slot name="tooltip">${this.label}</slot>
              </nord-tooltip>
            `
          : nothing}
      </div>
    `
  }

  connectedCallback() {
    super.connectedCallback()
    this.startObservingAside()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.stopObservingAside()
  }

  @observe('drawer')
  protected handleDrawerChange() {
    this.syncFromAside()
  }

  @observe('href')
  protected handleHrefChange() {
    this.syncFromAside()
  }

  @observe('disabled')
  protected handleDisabledChange() {
    this.syncAria()
  }

  private get aside(): HTMLElement | null {
    return this.closest('nord-aside')
  }

  /**
   * `href` takes precedence over `drawer`: a trigger with both acts as a
   * link and never toggles a drawer.
   */
  private get isLink(): boolean {
    return !!this.href
  }

  private readonly handleActiveDrawerChange = () => {
    this.syncFromAside()
  }

  /**
   * Listen to the aside's own `active-drawer-change` event instead of each
   * trigger installing its own subtree MutationObserver on the whole aside.
   * One shared event covers open / switch / close, so N triggers no longer
   * mean N observers waking on every aside DOM mutation.
   */
  private startObservingAside() {
    this.observedAside = this.aside ?? undefined
    if (!this.observedAside) {
      return
    }
    this.observedAside.addEventListener('active-drawer-change', this.handleActiveDrawerChange)
    this.syncFromAside()
  }

  private stopObservingAside() {
    this.observedAside?.removeEventListener('active-drawer-change', this.handleActiveDrawerChange)
    this.observedAside = undefined
  }

  private syncFromAside() {
    const aside = this.observedAside ?? this.aside
    if (this.isLink || !aside) {
      this.active = false
      this.targetDisabled = false
      this.syncAria()
      return
    }

    // Prefer the live `activeDrawer` property over the reflected attribute:
    // the `active-drawer-change` event fires during the aside's update cycle,
    // before Lit reflects the property to the `active-drawer` attribute, so
    // reading the attribute here would lag one tick behind. Fall back to the
    // attribute only while the aside element has not upgraded yet.
    const activeDrawer
      = (aside as HTMLElement & { activeDrawer?: string }).activeDrawer
        ?? aside.getAttribute('active-drawer')
        ?? ''
    this.active = !!this.drawer && activeDrawer === this.drawer

    const target = this.drawer
      ? aside.querySelector(`:scope > nord-aside-drawer#${CSS.escape(this.drawer)}`)
      : null
    this.targetDisabled = !target || target.hasAttribute('disabled')

    this.syncAria()
  }

  private syncAria() {
    // Link mode has no expand/collapse semantics — no aria-controls or
    // aria-expanded, and the inner nord-button anchor handles disabled
    // link semantics itself.
    if (this.drawer && !this.isLink) {
      this.setAttribute('aria-controls', this.drawer)
    }
    else {
      this.removeAttribute('aria-controls')
    }
    if (this.isLink) {
      this.removeAttribute('aria-expanded')
    }
    else {
      this.setAttribute('aria-expanded', this.active ? 'true' : 'false')
    }
    if (this.disabled || this.targetDisabled) {
      this.setAttribute('aria-disabled', 'true')
    }
    else {
      this.removeAttribute('aria-disabled')
    }
  }

  private handleClick = (event: Event) => {
    if (this.disabled || this.targetDisabled) {
      event.stopPropagation()
      event.preventDefault()
      return
    }
    const aside = this.aside
    if (this.isLink || !aside || !this.drawer) {
      return
    }
    const current = aside.getAttribute('active-drawer') ?? ''
    ;(aside as HTMLElement & { activeDrawer?: string }).activeDrawer
      = current === this.drawer ? '' : this.drawer
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-aside-trigger': AsideTrigger
  }
}
